import { useCallback, useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import { logToNewRelic } from '../../services/debug';
import { devError, devLog } from '../../utils/log';
import { EnumOpenDialog } from '../ui/types';
import { useStores } from '../useStores';

import type { FeatureManagerDialogType } from './types';

export const useFeatureManager = () => {
  const {
    authStore,
    gameStore,
    featureManagerStore,
    coinTypeStore,
    uiStore,
    portalStore,
    payRedeemStore,
    minigamesStore,
  } = useStores();

  const [searchParams] = useSearchParams();

  const { userCoolDown } = authStore;

  const preventDialogs = !!gameStore.gameId || minigamesStore.isDialogOpen || minigamesStore.loadingMinigame;

  const purchaseMethod = searchParams.get('method'); // After purchase with skrill

  const onFeatureReady = useCallback(
    async (featureType: FeatureManagerDialogType) => {
      try {
        if (preventDialogs) {
          devLog('FeatureManager onFeatureReady - preventDialogs is true, skipping dialog start');
          featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
          return;
        }

        const dialogStarted = await featureManagerStore.startDialogs(featureType);
        const nextSequenceState = dialogStarted ? 'FEATURE_IN_PROGRESS' : 'CHECK_QUEUE_STATE';
        featureManagerStore.setSequenceState(nextSequenceState);
        devLog('startFeature', { featureType, dialogStarted, nextSequenceState });
      } catch (e) {
        devError('Error starting dialog', { featureType, e });
        featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
      }
    },
    [featureManagerStore, preventDialogs],
  );

  useEffect(() => {
    devLog(
      `FeatureManager readyForFeaturesSequence: ${featureManagerStore.readyForFeaturesSequence}, sequenceState: ${featureManagerStore.sequenceState}`,
    );

    if (featureManagerStore.readyForFeaturesSequence && featureManagerStore.sequenceState === 'PRE_START') {
      featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
    }
  }, [featureManagerStore, featureManagerStore.readyForFeaturesSequence, featureManagerStore.sequenceState]);

  useEffect(() => {
    if (userCoolDown) {
      uiStore.setOpenDialog({
        dialog: EnumOpenDialog.isOpenCoolDownDialog,
      });

      return;
    }

    if (featureManagerStore.storeStatus === 'READY') {
      void featureManagerStore.fetchStores({ isCoinChange: false });
      featureManagerStore.startMainSequence();
    }
    // preventDialogs is deliberately not a dependency: this effect never reads it, and including
    // it re-ran the full 22-store fetchStores fan-out on every minigame open and close. The
    // dialog-suppression guards that do read it live in onFeatureReady and the sequence effect.
  }, [featureManagerStore, featureManagerStore.storeStatus, userCoolDown, uiStore]);

  // On coinType change effect, restart the sequence
  useEffect(() => {
    if (userCoolDown) {
      return;
    }

    if (coinTypeStore.coinType !== featureManagerStore.currentCoinType) {
      const coinType = coinTypeStore.coinType;
      featureManagerStore.setCoinType(coinType);
      void featureManagerStore.fetchStores({ isCoinChange: true });
      featureManagerStore.startMainSequence();
    }
  }, [coinTypeStore.coinType, featureManagerStore, featureManagerStore.currentCoinType, userCoolDown]);

  const isThereAnyDialogOpen = Boolean(
    uiStore.openDialog.dialog || uiStore.openFullPageDialog || portalStore.isVisible,
  );

  useEffect(() => {
    if (!featureManagerStore.readyForFeaturesSequence) {
      devLog('FeatureManager not ready for sequence yet');
      return;
    }

    if (preventDialogs) {
      devLog('FeatureManager preventDialogs is true, skipping sequence');
      return;
    }

    if (payRedeemStore.afterPurchaseFlowInProgress || purchaseMethod) {
      devLog('FeatureManager afterPurchaseFlowInProgress');
      return;
    }

    if (isThereAnyDialogOpen) {
      devLog('FeatureManager another dialog is open');
      return;
    }

    switch (featureManagerStore.sequenceState) {
      case 'OFF': {
        devLog('FeatureManager OFF state');
        if (!featureManagerStore.isQueueEmpty) {
          featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
        }
        break;
      }
      case 'PRE_START': {
        devLog('FeatureManager PRE_START STEP');
        break;
      }
      case 'CHECK_QUEUE_STATE': {
        devLog('FeatureManager CHECK_QUEUE_STATE STEP');
        const nextFeatureType = featureManagerStore.shiftFeatureType();
        if (nextFeatureType) {
          featureManagerStore.setSequenceState('FEATURE_START');
        } else {
          featureManagerStore.setSequenceState('OFF');
        }
        break;
      }
      case 'FEATURE_START': {
        const currentFeatureType = featureManagerStore.getCurrentFeatureType();
        const featureStatus = featureManagerStore.getFeatureStatus();

        if (!currentFeatureType) {
          devError('FeatureManager currentFeatureType is null');
          featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
          return;
        }

        devLog(`FEATURE_START featureType: ${currentFeatureType}, status: ${featureStatus}`);

        switch (featureStatus) {
          case 'OFF': {
            featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
            break;
          }
          case 'READY': {
            void onFeatureReady(currentFeatureType);
            break;
          }
          case 'ERROR': {
            //TODO: notify Slack or new relic
            devError(`FeatureManager feature ${currentFeatureType} is in ERROR state`);
            logToNewRelic('FeatureManager', `FeatureManager error ${JSON.stringify({ currentFeatureType })}`, 'error');
            featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');
            break;
          }
          case 'FETCHING': {
            devLog(`FeatureManager feature ${currentFeatureType} is in FETCHING state`);
            featureManagerStore.setSequenceState('CHECK_QUEUE_STATE');

            break;
          }
        }
        break;
      }

      case 'FEATURE_IN_PROGRESS': {
        const featureType = featureManagerStore.getCurrentFeatureType();
        devLog(`DIALOG_IN_PROGRESS`, { featureType });
        break;
      }
    }
  }, [
    featureManagerStore,
    featureManagerStore.sequenceState,
    featureManagerStore.isQueueEmpty,
    featureManagerStore.readyForFeaturesSequence,
    onFeatureReady,
    isThereAnyDialogOpen,
    payRedeemStore.afterPurchaseFlowInProgress,
    purchaseMethod,
    preventDialogs,
  ]);
};
